import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { AppHeader } from "@/components/AppHeader";
import { useProfile, useIsAdmin } from "@/lib/auth";
import { supabase } from "@/integrations/supabase/client";
import { User, Wallet, ClipboardList, LogOut, Shield, ChevronRight, Trophy } from "lucide-react";
import { ReportProblemDialog } from "@/components/ReportProblemDialog";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";

export const Route = createFileRoute("/_authenticated/profile")({
  head: () => ({ meta: [{ title: "Profile — Brothers Club" }] }),
  component: ProfilePage,
});

function ProfilePage() {
  const { data: profile } = useProfile();
  const { data: isAdmin } = useIsAdmin();
  const navigate = useNavigate();
  const qc = useQueryClient();

  async function signOut() {
    await qc.cancelQueries();
    qc.clear();
    await supabase.auth.signOut();
    toast.success("Signed out");
    navigate({ to: "/auth", replace: true });
  }

  const items = [
    { to: "/wallet", label: "Wallet", icon: Wallet },
    { to: "/history", label: "History", icon: ClipboardList },
    { to: "/matches", label: "My Games", icon: Trophy },
    { to: "/results", label: "Results", icon: ClipboardList },
  ] as const;

  return (
    <div>
      <AppHeader showMenu={false} />
      <div className="gradient-header px-4 pb-8 pt-2 -mt-1">
        <div className="flex items-center gap-3 text-primary-foreground">
          <div className="grid h-16 w-16 shrink-0 place-items-center rounded-full bg-white/15 text-2xl font-black">
            {profile?.name?.[0]?.toUpperCase() ?? <User className="h-8 w-8" />}
          </div>
          <div className="min-w-0">
            <p className="truncate text-lg font-black">{profile?.name ?? "Player"}</p>
            <p className="text-xs text-white/70">{profile?.phone ?? profile?.email ?? ""}</p>
          </div>
        </div>
      </div>

      <div className="space-y-2 px-4 py-4">
        <div className="rounded-2xl bg-card p-4 shadow-card">
          <p className="text-xs uppercase text-muted-foreground">Wallet Balance</p>
          <p className="mt-1 text-2xl font-black text-primary">₹ {Number(profile?.wallet_balance ?? 0).toLocaleString("en-IN")}</p>
        </div>

        <div className="overflow-hidden rounded-2xl bg-card shadow-card">
          {items.map((it, i) => {
            const Icon = it.icon;
            return (
              <Link key={it.to} to={it.to} className={`flex items-center justify-between px-4 py-3.5 ${i > 0 ? "border-t border-border" : ""}`}>
                <span className="flex items-center gap-3">
                  <span className="grid h-9 w-9 place-items-center rounded-lg bg-primary/10 text-primary"><Icon className="h-4 w-4" /></span>
                  <span className="text-sm font-semibold">{it.label}</span>
                </span>
                <ChevronRight className="h-4 w-4 text-muted-foreground" />
              </Link>
            );
          })}
        </div>

        {isAdmin && (
          <Link to="/admin" className="flex items-center justify-between rounded-2xl bg-primary/5 border border-primary/30 p-4">
            <span className="flex items-center gap-3">
              <span className="grid h-9 w-9 place-items-center rounded-lg bg-primary text-primary-foreground"><Shield className="h-4 w-4" /></span>
              <span className="text-sm font-bold text-primary">Admin Panel</span>
            </span>
            <ChevronRight className="h-4 w-4 text-primary" />
          </Link>
        )}

        <ReportProblemDialog />

        <button onClick={signOut} className="flex w-full items-center gap-3 rounded-2xl bg-destructive/10 p-4 text-destructive">
          <span className="grid h-9 w-9 place-items-center rounded-lg bg-destructive text-destructive-foreground"><LogOut className="h-4 w-4" /></span>
          <span className="text-sm font-bold">Sign out</span>
        </button>

        <div className="h-4" />
      </div>
    </div>
  );
}
